// src/helpers/infografia/imageProxyHelper.js

/**
 * Convierte una URL del CDN o un key a la ruta del proxy CORS del backend
 * @param {string} cdnUrl - URL completa del CDN (ej: https://petitprime-images.fra1.cdn.digitaloceanspaces.com/infografias/...)
 * @param {string} key - Key de la imagen en el CDN (ej: infografias/65a1b2c3d4e5f6g7h8i9j0k1/elementos/imagen-123.jpg)
 * @returns {string} - Ruta del proxy sin /api (ej: /infografias/imagen/infografias/65a1b2c3d4e5f6g7h8i9j0k1/elementos/imagen-123.jpg)
 */
export const getProxyImagePath = (cdnUrl, key = null) => {
    // Si tenemos el key directamente, usarlo
    if (key) {
        // El key puede venir con barras, NO codificar las barras ya que el backend las espera como parte de la ruta
        // El backend espera: /api/infografias/imagen/infografias/692ac0f2cd85aac4bc06dfd4/elementos/imagen.jpg
        // NO codificar el key completo, solo asegurarnos de que esté limpio
        const cleanKey = key.trim();
        return `/infografias/imagen/${cleanKey}`;
    }

    // Si no hay key, intentar extraerlo de la URL del CDN
    if (!cdnUrl) {
        return null;
    }

    // Patrones comunes de URLs de Digital Ocean Spaces
    // https://petitprime-images.fra1.cdn.digitaloceanspaces.com/infografias/...
    // https://petitprime-images.fra1.digitaloceanspaces.com/infografias/...
    const patterns = [
        /digitaloceanspaces\.com\/(.+)$/i,  // Específico para Digital Ocean Spaces (prioritario)
        /cdn\.digitaloceanspaces\.com\/(.+)$/i,  // Específico para CDN de Digital Ocean Spaces (prioritario)
        /https?:\/\/[^\/]+\/(.+)$/  // Cualquier dominio seguido de la ruta (fallback)
    ];

    for (const pattern of patterns) {
        const match = cdnUrl.match(pattern);
        if (match && match[1]) {
            const extractedKey = match[1];
            // Decodificar si está codificado
            const decodedKey = decodeURIComponent(extractedKey);
            // NO codificar el key, el backend espera las barras sin codificar
            return `/infografias/imagen/${decodedKey}`;
        }
    }

    // Si no se puede extraer el key, devolver null
    return null;
};

/**
 * Reemplaza temporalmente las URLs de las imágenes en el canvas con URLs del proxy
 * @param {object} fabricCanvas - Instancia del canvas de Fabric.js
 * @param {function} getImageKey - Función que obtiene el key de una imagen desde sus propiedades
 * @returns {Array} - Array de objetos con información de las imágenes reemplazadas (para restaurar después)
 */
export const replaceCanvasImagesWithProxy = (fabricCanvas, getImageKey = null) => {
    if (!fabricCanvas) {
        return [];
    }

    const replacedImages = [];
    const objects = fabricCanvas.getObjects();

    objects.forEach((obj) => {
        // Solo procesar objetos de tipo imagen
        if (obj.elementType === 'imagen' && obj.elementProperties) {
            const props = obj.elementProperties;
            const originalUrl = props.cdnUrl || props.url;
            const imageKey = getImageKey ? getImageKey(props) : props.key;

            if (originalUrl || imageKey) {
                const proxyPath = getProxyImagePath(originalUrl, imageKey);

                if (proxyPath) {
                    // Guardar información para restaurar después
                    replacedImages.push({
                        object: obj,
                        originalUrl: originalUrl,
                        proxyPath: proxyPath
                    });

                    // Reemplazar la URL en el objeto (esto no afecta la imagen ya cargada,
                    // pero podemos usar esta información si necesitamos recargar)
                    // Nota: En Fabric.js, una vez que la imagen está cargada, cambiar la URL
                    // no la recarga automáticamente. Necesitamos recargar la imagen.
                    // Por ahora, guardamos la información para uso futuro.
                }
            }
        }
    });

    return replacedImages;
};

/**
 * Recarga todas las imágenes del canvas usando URLs del proxy
 * @param {object} fabricCanvas - Instancia del canvas de Fabric.js
 * @param {function} getImageKey - Función que obtiene el key de una imagen desde sus propiedades
 * @returns {Promise<void>}
 */
export const reloadCanvasImagesWithProxy = async (fabricCanvas, getImageKey = null) => {
    if (!fabricCanvas) {
        return;
    }

    // Importar FabricImage dinámicamente
    const fabric = await import('fabric');
    const FabricImage = fabric.Image || fabric.FabricImage;
    const objects = fabricCanvas.getObjects();
    const imageObjects = objects.filter(obj => obj.elementType === 'imagen' && obj.elementProperties);

    // Procesar imágenes una por una para evitar problemas de concurrencia
    for (const obj of imageObjects) {
        const props = obj.elementProperties;
        const originalUrl = props.cdnUrl || props.url;
        // Intentar obtener el key: primero de la función, luego de props.key, luego extraer de la URL
        let imageKey = getImageKey ? getImageKey(props) : props.key;

        // Si no hay key pero hay URL, intentar extraerlo de la URL
        if (!imageKey && originalUrl) {
            // Extraer el key de la URL del CDN
            const keyMatch = originalUrl.match(/(?:digitaloceanspaces\.com\/|cdn\.digitaloceanspaces\.com\/)(.+)$/i);
            if (keyMatch && keyMatch[1]) {
                imageKey = decodeURIComponent(keyMatch[1]);
            }
        }

        if (originalUrl || imageKey) {
            const proxyPath = getProxyImagePath(originalUrl, imageKey);

            if (proxyPath) {
                try {
                    // Obtener la URL base de la API (ya incluye /api)
                    const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:3000/api';
                    // Construir URL completa: baseUrl ya tiene /api, proxyPath es /infografias/imagen/...
                    const fullProxyUrl = `${baseUrl}${proxyPath}`;

                    // Obtener token para autenticación
                    let token = null;
                    try {
                        const storedData = localStorage.getItem('auth-storage-petitprime');
                        if (storedData) {
                            const parsedData = JSON.parse(storedData);
                            token = parsedData?.state?.token;
                        }
                    } catch (error) {
                        console.warn('Error accessing localStorage:', error);
                    }

                    // Fabric.js fromURL no soporta headers personalizados directamente
                    // Necesitamos usar fetch primero para obtener la imagen con autenticación
                    const headers = {};
                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }

                    // Obtener imagen como blob con autenticación

                    const response = await fetch(fullProxyUrl, { headers });

                    if (!response.ok) {
                        const errorText = await response.text().catch(() => '');

                        throw new Error(`Error cargando imagen: ${response.status} - ${errorText || response.statusText}`);
                    }
                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);

                    // Crear imagen de Fabric.js desde el blob URL
                    const newImg = await FabricImage.fromURL(blobUrl, {
                        crossOrigin: 'anonymous'
                    });

                    // Limpiar el blob URL después de cargar
                    URL.revokeObjectURL(blobUrl);

                    // Copiar propiedades del objeto original usando set individual para cada propiedad
                    // Esto evita problemas con el método set en Fabric.js v6
                    newImg.left = obj.left || 0;
                    newImg.top = obj.top || 0;
                    newImg.scaleX = obj.scaleX || 1;
                    newImg.scaleY = obj.scaleY || 1;
                    newImg.opacity = obj.opacity !== undefined ? obj.opacity : 1;
                    newImg.selectable = obj.selectable !== undefined ? obj.selectable : true;
                    newImg.hasControls = obj.hasControls !== undefined ? obj.hasControls : true;
                    newImg.hasBorders = obj.hasBorders !== undefined ? obj.hasBorders : true;

                    // Copiar propiedades personalizadas
                    newImg.elementId = obj.elementId;
                    newImg.elementType = obj.elementType;
                    newImg.elementProperties = obj.elementProperties;
                    newImg.hasCORS = true;

                    // Reemplazar objeto en el canvas: eliminar el antiguo y añadir el nuevo
                    // Usar add en lugar de insertAt para evitar problemas con Fabric.js v6
                    fabricCanvas.remove(obj);
                    fabricCanvas.add(newImg);

                    // Renderizar después de cada reemplazo para evitar problemas
                    fabricCanvas.renderAll();
                } catch (error) {
                    console.error('Error recargando imagen con proxy:', error);
                    // Si falla, mantener la imagen original
                }
            }
        }
    }

    // Renderizar una vez más al final para asegurar que todo esté actualizado
    fabricCanvas.renderAll();
};

